/* Copy da gestão de admin-push de notificações (ADR-0687). Rótulos de status/severidade/expiração
   vêm de enums fixos do contrato (não deduzidos dos dados) — ver `use-admin-notifications.js`. */
import type {
  UserNotificationExpiryFilter,
  UserNotificationSeverity,
  UserNotificationStatus,
} from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface NotificationsContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly newNotificationButton: string;
  readonly searchAriaLabel: string;
  readonly searchPlaceholder: string;
  readonly statusFilterAriaLabel: string;
  readonly audienceFilterAriaLabel: string;
  readonly expiryFilterAriaLabel: string;
  readonly severityFacetLabel: string;
  readonly allLabel: string;
  readonly statusLabels: Readonly<Record<UserNotificationStatus, string>>;
  readonly severityLabels: Readonly<Record<UserNotificationSeverity, string>>;
  readonly expiryLabels: Readonly<Record<UserNotificationExpiryFilter, string>>;
  readonly audienceAnyLabel: string;
  readonly audienceGeneralLabel: string;
  readonly audienceTargetedLabel: string;
  readonly expiryAllLabel: string;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly publishSuccessToast: string;
  readonly publishErrorToast: string;
  readonly deleteDialogTitle: string;
  readonly deleteDialogDescription: (title: string) => string;
  readonly deleteConfirmButton: string;
  readonly deleteSuccessToast: string;
  readonly deleteErrorToast: string;
  readonly bulkSelected: (count: number) => string;
  readonly bulkDelete: string;
  readonly bulkClear: string;
  readonly bulkTitle: string;
  readonly bulkDescription: (count: number) => string;
  readonly bulkConfirm: string;
  readonly bulkSuccess: (count: number) => string;
  readonly bulkPartial: (success: number, failed: number) => string;
  readonly bulkError: string;
  readonly selectPageAriaLabel: string;
  readonly selectRowAriaLabel: (title: string) => string;
}

const NOTIFICATIONS_CONTENT: Bilingual<NotificationsContent> = {
  'pt-BR': {
    pageTitle: 'Notificações',
    pageDescription: 'Avisos enviados aos usuários do app — composição, publicação e remoção.',
    newNotificationButton: 'Nova notificação',
    searchAriaLabel: 'Buscar notificações',
    searchPlaceholder: 'Buscar por título…',
    statusFilterAriaLabel: 'Filtrar por status',
    audienceFilterAriaLabel: 'Filtrar por público',
    expiryFilterAriaLabel: 'Filtrar por expiração',
    severityFacetLabel: 'Severidade',
    allLabel: 'Todos',
    statusLabels: { draft: 'Rascunho', published: 'Publicada' },
    severityLabels: { info: 'Info', success: 'Sucesso', warning: 'Aviso', error: 'Erro' },
    expiryLabels: { active: 'Vigentes', expired: 'Expiradas' },
    audienceAnyLabel: 'Todos',
    audienceGeneralLabel: 'Gerais',
    audienceTargetedLabel: 'Segmentados',
    expiryAllLabel: 'Todas',
    loadError: 'Falha ao carregar as notificações.',
    loadMoreButton: 'Carregar mais',
    publishSuccessToast: 'Notificação publicada.',
    publishErrorToast: 'Falha ao publicar.',
    deleteDialogTitle: 'Remover notificação',
    deleteDialogDescription: (title) => `Remover "${title}"? Esta ação não pode ser desfeita.`,
    deleteConfirmButton: 'Remover',
    deleteSuccessToast: 'Notificação removida.',
    deleteErrorToast: 'Falha ao remover.',
    bulkSelected: (count) => `${count} rascunho(s) selecionado(s)`,
    bulkDelete: 'Remover rascunhos',
    bulkClear: 'Limpar seleção',
    bulkTitle: 'Remover rascunhos selecionados',
    bulkDescription: (count) =>
      `Remover ${count} rascunho(s)? Notificações publicadas nunca são removidas por este lote.`,
    bulkConfirm: 'Remover rascunhos',
    bulkSuccess: (count) => `${count} rascunho(s) removido(s).`,
    bulkPartial: (success, failed) => `${success} removido(s); ${failed} com falha.`,
    bulkError: 'Falha ao remover os rascunhos selecionados.',
    selectPageAriaLabel: 'Selecionar rascunhos desta página',
    selectRowAriaLabel: (title) => `Selecionar rascunho ${title}`,
  },
  'en-US': {
    pageTitle: 'Notifications',
    pageDescription: 'Alerts sent to app users — composing, publishing, and removing.',
    newNotificationButton: 'New notification',
    searchAriaLabel: 'Search notifications',
    searchPlaceholder: 'Search by title…',
    statusFilterAriaLabel: 'Filter by status',
    audienceFilterAriaLabel: 'Filter by audience',
    expiryFilterAriaLabel: 'Filter by expiry',
    severityFacetLabel: 'Severity',
    allLabel: 'All',
    statusLabels: { draft: 'Draft', published: 'Published' },
    severityLabels: { info: 'Info', success: 'Success', warning: 'Warning', error: 'Error' },
    expiryLabels: { active: 'Active', expired: 'Expired' },
    audienceAnyLabel: 'All',
    audienceGeneralLabel: 'General',
    audienceTargetedLabel: 'Targeted',
    expiryAllLabel: 'All',
    loadError: 'Failed to load notifications.',
    loadMoreButton: 'Load more',
    publishSuccessToast: 'Notification published.',
    publishErrorToast: 'Failed to publish.',
    deleteDialogTitle: 'Remove notification',
    deleteDialogDescription: (title) => `Remove "${title}"? This action cannot be undone.`,
    deleteConfirmButton: 'Remove',
    deleteSuccessToast: 'Notification removed.',
    deleteErrorToast: 'Failed to remove.',
    bulkSelected: (count) => `${count} draft(s) selected`,
    bulkDelete: 'Remove drafts',
    bulkClear: 'Clear selection',
    bulkTitle: 'Remove selected drafts',
    bulkDescription: (count) =>
      `Remove ${count} draft(s)? Published notifications are never removed by this bulk action.`,
    bulkConfirm: 'Remove drafts',
    bulkSuccess: (count) => `${count} draft(s) removed.`,
    bulkPartial: (success, failed) => `${success} removed; ${failed} failed.`,
    bulkError: 'Failed to remove the selected drafts.',
    selectPageAriaLabel: 'Select drafts on this page',
    selectRowAriaLabel: (title) => `Select draft ${title}`,
  },
};

export const useNotificationsContent = (): NotificationsContent =>
  useBilingual(NOTIFICATIONS_CONTENT);
